import {
  BrandLogo,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  ThemeScope,
} from '@bia-auction/ui';
import { t } from '@bia-auction/shared-i18n';
import { Link, useSearch } from '@tanstack/react-router';
import { AlertCircle, CheckCircle2 } from 'lucide-react';
import { useCallback, useState, type FormEvent, type ReactNode } from 'react';
import { api, ApiError } from '../lib/api';
import { initialTurnstileToken, turnstileConfigured } from '../lib/turnstile';
import { Turnstile } from '../components/Turnstile';
import { BRAND } from '../lib/config';
import { PHOTOS } from '../lib/photos';

/**
 * Password reset (M19, audit Y-06 — there was no recovery path at all).
 *
 * Both screens are unauthenticated, so they carry the same fixed-dark photographic hero as
 * `LoginPage` (docs/15 §5, M18 photography) — a visitor who fails to sign in and lands here
 * should not feel they have left the site.
 *
 * Deliberately a near-copy of `web-admin`'s file: the flow and its copy are identical, only the
 * shell around it differs (this one renders inside the bidder's header/footer). Keep the two in
 * step.
 *
 * `POST /auth/password/forgot` answers 204 whether or not the address has an account, and this
 * page must not say more than that: a message reading "we've sent you a link" for an address
 * with no account is exactly the enumeration oracle the endpoint is written to avoid.
 */

/** The shared unauthenticated card, so the two screens cannot drift from each other or login. */
function AuthCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div
      className="flex flex-1 items-center justify-center bg-navy-900 bg-hero bg-cover bg-center px-4 py-16"
      // Layered, not swapped: `bg-navy-900` is the opaque floor, so a missing photograph leaves
      // the plain gradient instead of dropping the surface onto the page background.
      style={{ backgroundImage: `var(--grad-hero-veil), url(/img/${PHOTOS.brandPlate})` }}
    >
      <ThemeScope theme="dark" />
      <Card className="w-full max-w-md">
        <CardHeader>
          <BrandLogo variant="white" alt={BRAND} className="mb-2" />
          <CardTitle className="font-head text-display font-semibold">{title}</CardTitle>
        </CardHeader>
        <CardContent>{children}</CardContent>
      </Card>
    </div>
  );
}

function Alert({ kind, children }: { kind: 'error' | 'success'; children: ReactNode }) {
  const Icon = kind === 'error' ? AlertCircle : CheckCircle2;
  return (
    <p
      role={kind === 'error' ? 'alert' : 'status'}
      className={`flex items-start gap-2 rounded-md px-3 py-2 text-sm ${
        kind === 'error'
          ? 'bg-danger-tint text-danger-tint-fg'
          : 'bg-success-tint text-success-tint-fg'
      }`}
    >
      <Icon aria-hidden className="mt-0.5 h-4 w-4 shrink-0" />
      {children}
    </p>
  );
}

const BACK_LINK =
  'block rounded-sm text-center text-sm text-muted-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // The API guards this route with the `turnstile` middleware, which in production rejects a
  // request carrying no token. Same pattern as the register and payment forms: seed a
  // placeholder when unconfigured (dev/CI), hold submit until a configured widget has a real
  // token, and reset after a failed attempt — the token is single-use and is consumed either
  // way, so a second submit without a reset would fail on the token rather than the request.
  const [turnstileToken, setTurnstileToken] = useState(initialTurnstileToken());
  const [turnstileReset, setTurnstileReset] = useState(0);
  const onTurnstileToken = useCallback((tk: string) => setTurnstileToken(tk), []);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await api('/auth/password/forgot', {
        method: 'POST',
        body: { email, turnstileToken },
        auth: false,
      });
      setSent(true);
    } catch (err) {
      // Only a transport, Turnstile or rate-limit failure can land here — the endpoint itself
      // is a 204 for every address.
      setError(err instanceof ApiError ? err.message : t('reset.failed'));
      setTurnstileToken(initialTurnstileToken());
      setTurnstileReset((n) => n + 1);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthCard title={t('reset.forgotTitle')}>
      {sent ? (
        <div className="space-y-4">
          <Alert kind="success">{t('reset.forgotSent')}</Alert>
          <Link to="/login" className={BACK_LINK}>
            {t('reset.backToLogin')}
          </Link>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-4">
          <p className="text-sm text-muted-foreground">{t('reset.forgotIntro')}</p>
          <div className="space-y-1.5">
            <Label htmlFor="email">{t('login.email')}</Label>
            <Input
              id="email"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <Turnstile onToken={onTurnstileToken} resetKey={turnstileReset} />
          {error && <Alert kind="error">{error}</Alert>}
          <Button
            type="submit"
            variant="brand"
            className="w-full"
            disabled={busy || (turnstileConfigured && !turnstileToken)}
          >
            {busy ? t('login.submitting') : t('reset.forgotSubmit')}
          </Button>
          <Link to="/login" className={BACK_LINK}>
            {t('reset.backToLogin')}
          </Link>
        </form>
      )}
    </AuthCard>
  );
}

export function ResetPasswordPage() {
  const { token } = useSearch({ strict: false }) as { token?: string };
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    // Checked here as well as by the server: the mismatch and the length rule are the two the
    // user can fix without a round trip, and `min(8)` mirrors `resetPasswordSchema`.
    if (password.length < 8) return setError(t('reset.tooShort'));
    if (password !== confirm) return setError(t('reset.mismatch'));
    setBusy(true);
    try {
      await api('/auth/password/reset', { method: 'POST', body: { token, password }, auth: false });
      setDone(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('reset.failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthCard title={t('reset.resetTitle')}>
      {done ? (
        <div className="space-y-4">
          <Alert kind="success">{t('reset.done')}</Alert>
          <Button asChild variant="brand" className="w-full">
            <Link to="/login">{t('reset.backToLogin')}</Link>
          </Button>
        </div>
      ) : !token ? (
        // A link that lost its token can only be restarted — offering a form that is certain
        // to 400 would waste the reader's time and their new password.
        <div className="space-y-4">
          <Alert kind="error">{t('reset.missingToken')}</Alert>
          <Button asChild variant="outline" className="w-full">
            <Link to="/forgot-password">{t('reset.forgotSubmit')}</Link>
          </Button>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="password">{t('reset.newPassword')}</Label>
            <Input
              id="password"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="confirm">{t('reset.confirmPassword')}</Label>
            <Input
              id="confirm"
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
            />
          </div>
          {error && <Alert kind="error">{error}</Alert>}
          <Button type="submit" variant="brand" className="w-full" disabled={busy}>
            {busy ? t('login.submitting') : t('reset.submit')}
          </Button>
        </form>
      )}
    </AuthCard>
  );
}
